/*
 * Utility helpers for adapting local JHA state into the API payload expected by the backend.
 * The frontend keeps a richer shape (for UI convenience) which needs to be normalized
 * before it is sent to Symfony. This module converts steps 1-4 into the new structure
 * without mutating the original objects.
 */

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const toArray = (value) => (Array.isArray(value) ? value : []);
const deepClone = (value) => (value === undefined ? undefined : JSON.parse(JSON.stringify(value)));

const toNonEmptyString = (value) => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : '';
    }
    return '';
};

const extractName = (input) => {
    if (!input) return '';
    if (typeof input === 'string') {
        return input.trim();
    }
    if (isPlainObject(input)) {
        return (
            input.FullName ||
            input.fullName ||
            input.name ||
            input.displayName ||
            ''
        ).toString().trim();
    }
    return '';
};

const normalizeDateToIso = (value) => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toISOString();
};

const normalizeStatus = (status) => {
    if (!status) return 'STATUS_NEW';
    const normalized = status.toString().toUpperCase();
    if (normalized.includes('FINISH') || normalized.includes('COMPLETE')) {
        return 'STATUS_FINISH';
    }
    if (normalized.includes('PROCESS') || normalized.includes('SUBMIT')) {
        return 'STATUS_PROCESS';
    }
    if (normalized.includes('SAVED') || normalized.includes('DRAFT')) {
        return 'STATUS_NEW';
    }
    if (normalized.startsWith('STATUS_')) {
        return normalized;
    }
    return 'STATUS_NEW';
};

const normalizeEmployees = (employeesSelected) => {
    return toArray(employeesSelected)
        .map((employee) => {
            const source = employee?.original ? employee.original : employee;
            if (!source) return null;

            const typeRaw = source._type || source.type || source.EmployeeType || '';
            const normalizedType = typeRaw.toString().toLowerCase().includes('temp') ? 'temp' : 'employee';
            const fullName = extractName(source);
            if (!fullName) return null;

            const normalized = {
                _type: normalizedType,
                FullName: fullName,
            };

            const division = source.Division ?? source.division ?? source.DivisionAgency ?? null;
            const agency = source.Agency ?? source.agency ?? null;
            const employeeId = source.EmployeeId ?? source.employeeId ?? null;
            const tempId = source.TempId ?? source.tempId ?? null;

            if (division) normalized.Division = division;
            if (agency) normalized.Agency = agency;
            if (employeeId) normalized.EmployeeId = employeeId;
            if (tempId) normalized.TempId = tempId;

            return normalized;
        })
        .filter(Boolean);
};

const normalizeDocsList = (docs) => {
    return toArray(docs).map((doc) => {
        const value = toNonEmptyString(doc?.value || doc);
        const required = doc?.required;
        const normalizedRequired = required === 'Yes Req' || required === true
            ? 'Yes Req'
            : required === 'Not Req' || required === false
                ? 'Not Req'
                : null;

        const normalizedDoc = {
            value,
            required: normalizedRequired,
        };

        if (doc?.attachments) {
            normalizedDoc.attachments = deepClone(doc.attachments);
        }

        // Preserve linked document UUIDs
        if (doc?.linkedCsepUuid) {
            normalizedDoc.linkedCsepUuid = doc.linkedCsepUuid;
        }
        if (doc?.linkedLttUuid) {
            normalizedDoc.linkedLttUuid = doc.linkedLttUuid;
        }
        if (doc?.linkedFarpUuid) {
            normalizedDoc.linkedFarpUuid = doc.linkedFarpUuid;
        }

        return normalizedDoc;
    });
};

const normalizeEvaluation = (evaluation) => {
    return toArray(evaluation).map((item) => {
        const value = toNonEmptyString(item?.value || item);
        const selected = item?.selected
            ? item.selected.toString().toUpperCase()
            : '';
        const normalizedSelected = ['YES', 'NO', 'N/A'].includes(selected) ? selected : '';
        return {
            value,
            selected: normalizedSelected,
        };
    });
};

const normalizeEnergyHazard = (hazard) => {
    if (!hazard) return null;
    const clone = deepClone(hazard) || {};
    const hazardValue = toNonEmptyString(
        clone.value || clone.name || clone.hazard || clone.label || ''
    );
    const description = toNonEmptyString(
        clone.description || clone.hazardDescription || clone.details || ''
    );
    const preventListRaw = clone.preventHazards
        ?? clone.prevent
        ?? clone.prevents
        ?? clone.controlMeasures
        ?? clone.controls
        ?? [];

    const preventList = toArray(preventListRaw)
        .map((item) => {
            if (typeof item === 'string') {
                return toNonEmptyString(item);
            }
            if (isPlainObject(item)) {
                return toNonEmptyString(item.value || item.name || item.prevent || '');
            }
            return '';
        })
        .filter(Boolean);

    return {
        ...clone,
        value: hazardValue,
        description,
        preventHazards: preventList,
    };
};

const normalizeEnergySelections = (row) => {
    const selections = toArray(row?.energySelections);

    if (selections.length) {
        return selections.map((selection) => {
            const hazards = toArray(selection?.hazards)
                .map(normalizeEnergyHazard)
                .filter(Boolean);
            return {
                id: selection?.id || null,
                energyId: selection?.energyId ?? selection?.id ?? null,
                energyKey: selection?.energyKey ?? null,
                energyName: toNonEmptyString(selection?.energyName || selection?.value || ''),
                hazards,
            };
        });
    }

    if (row?.energyId || row?.energyName) {
        const hazards = toArray(row?.hazards)
            .map(normalizeEnergyHazard)
            .filter(Boolean);
        return [
            {
                id: row?.id || null,
                energyId: row?.energyId ?? null,
                energyKey: row?.energyKey ?? null,
                energyName: toNonEmptyString(row?.energyName || row?.energyId || ''),
                hazards,
            },
        ];
    }

    return [];
};

const normalizeEnergyExposures = (stepTwo) => {
    const rows = Array.isArray(stepTwo?.energyActivityRows) && stepTwo.energyActivityRows.length
        ? stepTwo.energyActivityRows
        : toArray(stepTwo?.energyExposures);

    const exposures = [];

    rows.forEach((row, rowIndex) => {
        const selections = normalizeEnergySelections(row);
        selections.forEach((selection, selectionIndex) => {
            const hazards = selection.hazards || [];
            const prevents = hazards.flatMap((hazard) => hazard?.preventHazards || []).filter(Boolean);

            exposures.push({
                rowId: row?.id ?? `row-${rowIndex}`,
                selectionId: selection?.id ?? `sel-${rowIndex}-${selectionIndex}`,
                workActivity: toNonEmptyString(row?.workActivity || ''),
                actions: toNonEmptyString(row?.actions || ''),
                energyId: selection?.energyId ? Number(selection.energyId) || selection.energyId : null,
                energyKey: selection?.energyKey || null,
                value: selection?.energyName || '',
                hazards,
                prevents,
            });
        });
    });

    return exposures.filter((exposure) =>
        exposure.value || exposure.energyId || exposure.hazards.length || exposure.prevents.length || exposure.workActivity
    );
};

const normalizeTasks = (existingTasks, exposures) => {
    const normalizedExisting = toArray(existingTasks)
        .map((task) => ({
            task: toNonEmptyString(task?.task || ''),
            taskCrewMembersResponsible: toNonEmptyString(task?.taskCrewMembersResponsible || ''),
            hazardsDescription: toNonEmptyString(task?.hazardsDescription || ''),
            hazardControlMeasures: toNonEmptyString(task?.hazardControlMeasures || ''),
        }))
        .filter((task) => task.task || task.hazardsDescription || task.hazardControlMeasures);

    if (normalizedExisting.length) {
        return normalizedExisting;
    }

    return exposures.map((exposure, index) => {
        const hazardNames = exposure.hazards
            .map((hazard) => hazard?.value || '')
            .filter(Boolean);
        const controlMeasures = exposure.prevents.filter(Boolean);

        return {
            task: exposure.workActivity || (hazardNames.length ? hazardNames.join(', ') : `Energy ${index + 1}`),
            taskCrewMembersResponsible: exposure.value || '',
            hazardsDescription: hazardNames.join('; '),
            hazardControlMeasures: controlMeasures.join('; '),
        };
    }).filter((task) => task.task || task.hazardsDescription || task.hazardControlMeasures);
};

const normalizeEmployeeSignatures = (signatures) => {
    return toArray(signatures)
        .map((signature) => {
            const employeeName = extractName(signature?.employeeName || signature?.FullName || signature?.name);
            const image = signature?.signature || signature?.image || '';
            if (!employeeName || !image) return null;

            const normalized = {
                employeeName,
                signature: image,
            };

            const signedAt = normalizeDateToIso(signature?.signedAt || signature?.date || signature?.timestamp);
            if (signedAt) {
                normalized.signedAt = signedAt;
            }

            if (signature?.initials) {
                normalized.initials = signature.initials;
            }

            if (signature?.employeeId) {
                normalized.employeeId = signature.employeeId;
            }

            if (signature?.tempId) {
                normalized.tempId = signature.tempId;
            }

            return normalized;
        })
        .filter(Boolean);
};

const normalizeChecklist = (stepFour) => {
    if (!stepFour) return null;
    const checklistEntries = Object.entries(stepFour.checklist || {}).reduce((acc, [key, value]) => {
        if (value === true || value === 'yes') {
            acc[key] = true;
        } else if (value === false || value === 'no') {
            acc[key] = false;
        }
        return acc;
    }, {});

    const notesEntries = Object.entries(stepFour.checklistNotes || {}).reduce((acc, [key, value]) => {
        if (checklistEntries[key] === false) {
            acc[key] = toNonEmptyString(value);
        }
        return acc;
    }, {});

    const supervisorSignature = stepFour.supervisorSignature
        || stepFour.signature
        || '';

    const completedAt = normalizeDateToIso(stepFour.completedAt);

    return {
        checklist: checklistEntries,
        checklistNotes: notesEntries,
        comments: toNonEmptyString(stepFour.comments || ''),
        supervisorSignature,
        completedAt,
    };
};

const normalizeStepOneNew = (stepOne) => {
    // NEW API structure: stepOne contains general info + evaluation
    return {
        mode: stepOne?.mode || 'job',
        locationName: toNonEmptyString(stepOne?.locationName || ''),
        jobNumber: toNonEmptyString(stepOne?.jobNumber || ''),
        jobSiteName: toNonEmptyString(stepOne?.jobSiteName || ''),
        projectName: toNonEmptyString(stepOne?.projectName || ''),
        jobSiteAddress: toNonEmptyString(stepOne?.jobSiteAddress || ''),
        taskLocation: toNonEmptyString(stepOne?.taskLocation || ''),
        date: stepOne?.date ? stepOne.date : '',
        personCompleting: extractName(stepOne?.personCompleting) || '',
        jobSiteSupervisor: extractName(stepOne?.jobSiteSupervisor) || '',
        evaluationOfWork: normalizeEvaluation(stepOne?.evaluationOfWork),
    };
};

const normalizeStepTwoNew = (stepTwo) => {
    // NEW API structure: stepTwo contains only documents
    return {
        docsPermits: normalizeDocsList(stepTwo?.docsPermits),
        customDocsPermits: normalizeDocsList(stepTwo?.customDocsPermits),
    };
};

const normalizeStepThreeNew = (stepThree) => {
    // NEW API structure: stepThree contains work activities + energy
    const normalized = {
        weatherConditions: toArray(stepThree?.weatherConditions).map((item) => toNonEmptyString(item)).filter(Boolean),
        tasks: toArray(stepThree?.tasks).map((item) => toNonEmptyString(item)).filter(Boolean),
        customTasks: toArray(stepThree?.customTasks).map((item) => toNonEmptyString(item)).filter(Boolean),
        sequenceOfJobSteps: toNonEmptyString(stepThree?.sequenceOfJobSteps || ''),
        preShiftSignature: stepThree?.preShiftSignature || stepThree?.supervisorSignature || '',
    };

    if (stepThree?.initialSubmission) {
        normalized.initialSubmission = deepClone(stepThree.initialSubmission);
    }

    normalized.energyExposures = normalizeEnergyExposures(stepThree);

    // Extract unique work activities from energy exposures rows
    const workActivitiesFromRows = toArray(stepThree?.energyActivityRows || stepThree?.energyExposures)
        .map(row => toNonEmptyString(row?.workActivity || ''))
        .filter(Boolean);

    // Combine all work activities and remove duplicates
    const allWorkActivities = Array.from(new Set([
        ...toArray(stepThree?.workActivities).map(item => toNonEmptyString(item)).filter(Boolean),
        ...toArray(stepThree?.customWorkActivities).map(item => toNonEmptyString(item)).filter(Boolean),
        ...workActivitiesFromRows
    ]));

    // Send all work activities - backend will check if exists in DB (standard) or save as custom
    normalized.workActivities = allWorkActivities;
    normalized.customWorkActivities = [];

    return normalized;
};

export const buildJhaPayload = (jha) => {
    if (!jha) return null;

    // NEW API structure - maps directly to frontend structure
    // Frontend: stepOne (general+eval), stepTwo (docs), stepThree (activities+energy), stepFour (crew), stepFive (review)
    // API: stepOne (general+eval), stepTwo (docs), stepThree (activities+energy), stepFour (crew - only in save, not initial), stepFive (review)

    const stepOne = normalizeStepOneNew(jha?.stepOne);
    const stepTwo = normalizeStepTwoNew(jha?.stepTwo);
    const stepThree = normalizeStepThreeNew(jha?.stepThree);

    const payload = {
        id: jha.id ?? null,
        localId: jha.localId ?? jha.id ?? null,
        status: normalizeStatus(jha.status),
        submittedAt: normalizeDateToIso(jha.submittedAt),
        createdAt: normalizeDateToIso(jha.createdAt),
        stepOne,
        stepTwo,
        stepThree,
    };

    // Add stepFour (crew) only for full saves, not for initial submission
    if (jha?.stepFour?.employeesSelected?.length) {
        payload.stepFour = {
            employeesSelected: normalizeEmployees(jha.stepFour.employeesSelected),
            employeeAcknowledgments: normalizeEmployeeSignatures(jha.stepFour.employeeAcknowledgments || [])
        };
    }

    // Add stepFive (review/checklist) if present
    const stepFive = normalizeChecklist(jha?.stepFive);
    if (stepFive) {
        payload.stepFive = stepFive;
    }

    if (jha.meta) {
        payload.meta = deepClone(jha.meta);
    }

    if (jha.syncStatus) {
        payload.syncStatus = jha.syncStatus;
    }

    if (jha.backendId) {
        payload.backendId = jha.backendId;
    }

    return payload;
};

export const buildJhaPayloadWithOverrides = (jha, overrides = {}) => {
    const payload = buildJhaPayload(jha) || {};
    return {
        ...payload,
        ...overrides,
        stepOne: overrides.stepOne ? overrides.stepOne : payload.stepOne,
        stepTwo: overrides.stepTwo ? overrides.stepTwo : payload.stepTwo,
        stepThree: overrides.stepThree ? overrides.stepThree : payload.stepThree,
        stepFour: overrides.stepFour ? overrides.stepFour : payload.stepFour,
        stepFive: overrides.stepFive ? overrides.stepFive : payload.stepFive,
    };
};

export default buildJhaPayload;
